import React, { FC, ReactNode } from "react";
import { Drinker } from "./Drinker";
import { toCaptilize } from "@/helpers/TimeFormat";
import { EVENT_TYPE_MAP, EventTypes, getGuestRangeFromValue, TypeOfServiceOptions } from "../constants/Options";

type Event = {
    eventAddress?: string;
    noOfGuests?: string;
    eventDate?: Date | null;
    startTime?: string;
    endTime?: string;
    drinker?: Drinker | string;
    estDrinks?: number | string;
    eventType?: string;
    eventLocation?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    typeOfService?: string;
    additionalInfo?: string;
};

interface Props {
    event: Event;
}

// keys for the "Event Details" group
type EventDetailKey =
    | "eventAddress"
    | "noOfGuests"
    | "time"
    | "drinker"
    | "eventDate"
    | "eventType"
    | "typeOfService"
    | "additionalInfo";

// default display order
const DEFAULT_ORDER: EventDetailKey[] = [
    "eventAddress",
    "noOfGuests",
    "time",
    "drinker",
    "eventDate",
    "eventType",
    "typeOfService",
    "additionalInfo"
];

const hasValue = (v: unknown) => v !== "" && v !== null && v !== undefined;
const DRINKER_LABELS: Record<Drinker, string> = {
    light: "Light",
    medium: "Standard",
    heavy: "Party",
};
const safeCapitalize = (v?: string | Drinker) =>
    typeof v === "string" && v.trim() ? toCaptilize(v) : "";
const getDrinkerLabel = (v?: string | Drinker) =>
    typeof v === "string" && v in DRINKER_LABELS
        ? DRINKER_LABELS[v as Drinker]
        : safeCapitalize(v);

const QuotePreview: FC<Props> = ({ event }) => {
    // Build entries with renderers and "present" checks
    const entries: Array<{
        key: EventDetailKey;
        present: boolean;
        node: ReactNode;
    }> = [
            {
                key: "eventAddress",
                present: hasValue(event.eventAddress),
                node: (
                    <p className="text-sm">
                        <span className="text-[#282828] font-[400] text-xs">Address:</span>{" "}
                        <span className="text-gray-500 text-xs">{event.eventAddress}</span>
                    </p>
                ),
            },
            {
                key: "noOfGuests",
                present: hasValue(event.noOfGuests),
                node: (
                    <p className="text-sm">
                        <span className="text-[#282828] font-[400] text-xs">No of guests:</span>{" "}
                        <span className="text-gray-500 text-xs">{getGuestRangeFromValue(Number(event.noOfGuests), 1, 25)}</span>
                    </p>
                ),
            },
            {
                key: "time",
                present: hasValue(event.startTime) || hasValue(event.endTime),
                node: (
                    <p className="text-sm">
                        <span className="text-[#282828] font-[400] text-xs">Time:</span>{" "}
                        <span className="text-gray-500 text-xs">
                            {event.startTime ?? ""} - {event.endTime ?? ""}
                        </span>
                    </p>
                ),
            },
            // {
            //     key: "drinker",
            //     present: hasValue(event.drinker),
            //     node: (
            //         <p className="text-sm">
            //             <span className="text-[#282828] font-[400] text-xs">Type of Drinker:</span>{" "}
            //             <span className="text-gray-500 text-xs">{getDrinkerLabel(event.drinker)}</span>
            //         </p>
            //     ),
            // },
            {
                key: "eventDate",
                present: hasValue(event.eventDate),
                node: (
                    <p className="text-sm">
                        <span className="text-[#282828] font-[400] text-xs">Event Date:</span>{" "}
                        <span className="text-gray-500 text-xs">
                            {event.eventDate?.toLocaleDateString()}
                        </span>
                    </p>
                ),
            },
            {
                key: "eventType",
                present: hasValue(event.eventType),
                node: (
                    <p className="text-sm">
                        <span className="text-[#282828] font-[400] text-xs">Event Type:</span>{" "}
                        {event.eventType && <span className="text-gray-500 text-xs">{safeCapitalize(EVENT_TYPE_MAP[event.eventType]?.name || '')}</span>}
                    </p>
                ),
            },
            {
                key: "typeOfService",
                present: hasValue(event.typeOfService),
                node: (
                    <p className="text-sm">
                        <span className="text-[#282828] font-[400] text-xs">Type of Service:</span>{" "}
                        {event.typeOfService && <span className="text-gray-500 text-xs"> {TypeOfServiceOptions.find((o) => o.value === event.typeOfService)?.name || ''}</span>}
                    </p>
                ),
            },
            {
                key: "additionalInfo",
                present: hasValue(event.additionalInfo),
                node: (
                    <p className="text-sm">
                        <span className="text-[#282828] font-[400] text-xs">Additional Information:</span>{" "}
                        <span className="text-gray-500 text-xs">{event.additionalInfo}</span>
                    </p>
                ),
            },
        ];

    // Only keep present fields and sort by fixed default order.
    // This achieves:
    // - first filled appears first,
    // - if a newly filled one is earlier in default order, it jumps ahead,
    // - once all present, the order == DEFAULT_ORDER.
    const sortedPresent = entries
        .filter((e) => e.present)
        .sort(
            (a, b) =>
                DEFAULT_ORDER.indexOf(a.key) - DEFAULT_ORDER.indexOf(b.key)
        );

    const hasAnyEventDetail = sortedPresent.length > 0;

    return (
        <div className="w-full max-w-2xl bg-white rounded-2xl">
            <h1 className="text-xl font-semibold text-gray-900 mb-4">Quote</h1>

            {/* Event Details (dynamic order, only those with values) */}
            {hasAnyEventDetail && (
                <div className="mb-4">
                    <div className="flex items-center gap-1 mb-2">
                        <span className="h-3 w-1 bg-orange-500 rounded" />
                        <h2 className="text-xs font-semibold  text-[#282828]">Event Details</h2>
                    </div>

                    {/* Layout stays 2 columns; items flow top-to-bottom in the chosen order */}
                    <div className="ml-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                        {sortedPresent.map((e) => (
                            <div key={e.key} className={e.key === "additionalInfo" ? "sm:col-span-2" : ""}>{e.node}</div>
                        ))}
                    </div>
                </div>
            )}

            {/* Client Details (kept simple: only render if present; you can apply same pattern if you want ordering logic) */}
            {(hasValue(event.firstName) ||
                hasValue(event.lastName) ||
                hasValue(event.email) ||
                hasValue(event.phone)) && (
                    <div>
                        <div className="flex items-center gap-1 mb-3">
                            <span className="h-3 w-1 bg-orange-500 rounded" />
                            <h2 className="text-xs font-semibold text-gray-900">Client Details</h2>
                        </div>

                        <div className="ml-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                            {hasValue(event.firstName) && (
                                // <div className="flex items-center gap-1 text-xs">
                                //     <span className="text-[#282828] font-[400]">
                                //         First Name:
                                //     </span>

                                //     <div className="group relative">
                                //         <span
                                //             className={`text-gray-500 truncate inline-block mt-1 ${event.firstName && event.firstName?.length > 20 ? 'max-w-[60px]' : 'max-w-none'}`}>
                                //             {event.firstName}
                                //         </span>

                                //         {event.firstName && event?.firstName?.length > 20 && (
                                //             <div className="absolute left-0 z-10 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 mt-1 whitespace-nowrap">
                                //                 {event.firstName}
                                //             </div>
                                //         )}
                                //     </div>
                                // </div>
                                <div className="text-xs">
                                    <span className="text-[#282828] font-[400] text-xs">Client First Name:</span>{" "}
                                    <span className="text-gray-500 text-xs">{event.firstName}</span>
                                </div>
                            )}
                            {hasValue(event.lastName) && (
                                <div className="text-xs">
                                    <span className="text-[#282828] font-[400] text-xs">Client Last Name:</span>{" "}
                                    <span className="text-gray-500 text-xs">{event.lastName}</span>
                                </div>
                            )}
                            {hasValue(event.email) && (
                                <div className="text-xs">
                                    <span className="text-[#282828] font-[400] text-xs">Client Email:</span>{" "}
                                    <span className="text-gray-500 text-xs">{event.email}</span>
                                </div>
                            )}
                            {hasValue(event.phone) && (
                                <div className="text-xs">
                                    <span className="text-[#282828] font-[400] text-xs">Client Phone Number:</span>{" "}
                                    <span className="text-gray-500 text-xs">{event.phone}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
        </div>
    );
};

export default QuotePreview;
